import { postData, sortFunc } from "../../shared/utils";

export function triageSquadViewModal({
  triageSelectionsUrl,
  triageSaveUrl,
  snapshotBaseUrl,
  minResponseThreshold,
}) {
  const { currentUser } = window;

  const triageCommentConfig = {
    noop: {
      label: "Add a comment for context",
      placeholder: "Add any additional context about this driver",
    },
    improvement_area: {
      label: "Add a comment for context",
      placeholder:
        "e.g. 'Planning to introduce planned weekly code review sessions'",
    },
    escalation: {
      label: "What support do you need?",
      placeholder:
        "e.g. 'Need dedicated time allocation for tech debt and buy-in from product'",
    },
  };

  return {
    triageCommentConfig,
    loaded: false,
    snapshotItems: [],
    responseCount: 0,
    minResponseThreshold,
    comments: [],
    commentsOpen: false,
    commentsDriver: null,
    commentsLoaded: false,
    triageComments: {}, // Triage comment text by item ID
    triageCommentOpenIds: {}, // Track which triage comment sections are open
    selections: {},
    submitting: false,
    showIncompleteSaveConfirmation: false,
    reusingPreviousResponses: false,
    compareTo: "vs_prev",
    showOrgComparison: true,
    init() {
      // Modal might be getting opened directly from triage=1 query param
      if (this.$store.triageSquadManager.squadViewModalOpen) {
        this.fetchData();
      }

      this.$watch("$store.triageSquadManager.squadViewModalOpen", () => {
        if (
          this.$store.triageSquadManager.snapshotSquadId &&
          this.$store.triageSquadManager.squadViewModalOpen
        ) {
          // Reset and fetch data
          this.loaded = false;
          this.snapshotItems = [];
          this.responseCount = 0;
          this.minResponseThreshold = minResponseThreshold;
          this.comments = [];
          this.commentsOpen = false;
          this.commentsDriver = null;
          this.commentsLoaded = false;
          this.triageComments = {};
          this.triageCommentOpenIds = {};
          this.selections = {};
          this.submitting = false;
          this.showIncompleteSaveConfirmation = false;
          this.reusingPreviousResponses = false;
          this.compareTo = "vs_prev";
          this.showOrgComparison = true;
          this.fetchData();
        }
      });
    },
    fetchData() {
      fetch(`${triageSelectionsUrl}?squad_id=${this.snapshotSquadId}`)
        .then((response) => response.json())
        .then((resp) => {
          resp.forEach((triageResult) => {
            this.selections[triageResult.factor_id] = triageResult.selection;

            // Load existing comment_text and auto-open section if comment exists
            if (triageResult.comment_text) {
              this.triageComments[triageResult.factor_id] =
                triageResult.comment_text;
              this.triageCommentOpenIds[triageResult.factor_id] = true;
            }

            if (!triageResult.submitted_by_user) {
              this.reusingPreviousResponses = true;
            }
          });

          this.fetchSnapshotItems();
        });
    },
    displayableItemCount(item) {
      if (currentUser.is_admin) {
        return item.comment_count - item.comment_count_flagged;
      } else {
        return (
          item.comment_count -
          item.comment_count_admin_only -
          item.comment_count_flagged
        );
      }
    },
    fetchSnapshotItems() {
      fetch(
        `${snapshotBaseUrl}/items?squad_ids=${this.snapshotSquadId}&public_and_private=true&branch=ALL`,
      )
        .then((response) => response.json())
        .then((resp) => {
          const sortedItems = resp.result_items
            .filter((i) => i.current_snapshot)
            .sort(sortFunc("priorityScoreAndCount"));

          const seenNames = [];
          const uniqueItems = [];
          sortedItems.forEach((item) => {
            if (!seenNames.includes(item.name.toLowerCase())) {
              uniqueItems.push(item);
              seenNames.push(item.name.toLowerCase());
            }
          });

          if (typeof resp.benchmark_factors == "object") {
            sortedItems.forEach((row) => {
              const benchmarkFactor = resp.benchmark_factors[row["id"]];

              if (
                benchmarkFactor &&
                row["score"] !== null &&
                row["score"] !== undefined
              ) {
                row["benchmark_50"] = benchmarkFactor["p_50"];
                row["benchmark_75"] = benchmarkFactor["p_75"];
                row["benchmark_90"] = benchmarkFactor["p_90"];

                row["vs_50th"] = row["score"] - benchmarkFactor["p_50"];
                row["vs_75th"] = row["score"] - benchmarkFactor["p_75"];
                row["vs_90th"] = row["score"] - benchmarkFactor["p_90"];
              }
            });
          }

          this.snapshotItems = uniqueItems.slice(0, 16);
          this.responseCount = resp.response_count;
          this.loaded = true;
        });
    },
    makeSelection(itemId, value) {
      this.selections[itemId] = value;
    },
    toggleTriageComment(itemId) {
      // Force reactivity by creating a new object
      this.triageCommentOpenIds = {
        ...this.triageCommentOpenIds,
        [itemId]: !this.triageCommentOpenIds[itemId],
      };
    },
    get hasAllSelections() {
      let valid = true;
      Object.values(this.snapshotItems).forEach((item) => {
        if (!this.selections[item.id]) valid = false;
      });
      return valid;
    },
    get isIncomplete() {
      return this.$store.triageSquadManager.incompleteSquads[
        this.snapshotSquadId
      ];
    },
    get snapshotSquadId() {
      return this.$store.triageSquadManager.snapshotSquadId;
    },
    get snapshotSquad() {
      return this.$store.triageSquadManager.snapshotSquads[
        this.snapshotSquadId
      ];
    },
    exitSquadViewModal() {
      if (this.$store.triageSquadManager.hasMultipleTeams) {
        // Return to multi team modal
        this.$store.triageSquadManager.squadViewModalOpen = false;
        this.$store.triageSquadManager.multiTeamModalOpen = true;
      } else {
        this.$store.triageSquadManager.squadViewModalOpen = false;
      }
    },
    save() {
      this.submitting = true;

      // Build triage results array with factor_id, selection, and comment_text
      const triageSelections = this.snapshotItems.map((item) => ({
        factor_id: item.id,
        selection: this.selections[item.id],
        comment_text: this.triageComments[item.id] || null,
      }));

      postData(`${triageSaveUrl}?squad_id=${this.snapshotSquadId}`, {
        triage_selections: triageSelections,
      }).then(() => {
        window.pushNotification({
          title: "Success!",
          desc: "Your responses were saved.",
        });

        if (this.$store?.triageSquadManager) {
          delete this.$store.triageSquadManager.incompleteSquads[
            this.snapshotSquadId
          ];
        }

        this.exitSquadViewModal();
        this.showIncompleteSaveConfirmation = false;
        this.reusingPreviousResponses = false;

        this.$nextTick(() => {
          this.submitting = false;
        });
      });
    },
    openComments(factorId) {
      this.commentsLoaded = false;
      this.commentsOpen = true;
      this.commentsDriver = this.snapshotItems.find((i) => i.id == factorId);

      fetch(
        `${snapshotBaseUrl}/comments?squad_ids=${this.snapshotSquadId}&factor_id=${factorId}&branch=ALL`,
      )
        .then((response) => response.json())
        .then((resp) => {
          this.comments = resp;
          this.commentsLoaded = true;
        });
    },
    get maxPriority() {
      const arr = this.snapshotItems.map((i) => i.priority);

      return Math.max(...arr);
    },
    get maxDriverScore() {
      const arr = this.snapshotItems.map((i) => i.score);

      return Math.max(...arr);
    },
    get completedCount() {
      return this.snapshotItems.filter((item) => this.selections[item.id])
        .length;
    },
    get totalCount() {
      return this.snapshotItems.length;
    },
    get completionPercentage() {
      if (this.totalCount === 0) return 0;
      return Math.round((this.completedCount / this.totalCount) * 100);
    },
    percentageOfResponses(item, positionCount) {
      if (item.count === 0) return "0%";

      return `${Math.round((100 * item[positionCount]) / item.count)}%`;
    },
    changeComparison(compareTo) {
      this.compareTo = compareTo;
    },
    calculateItemComparison(item) {
      if (this.compareTo == "vs_50th") {
        if (item.benchmark_50 == null) return null;
        return item.score - item.benchmark_50;
      } else if (this.compareTo == "vs_75th") {
        if (item.benchmark_75 == null) return null;
        return item.score - item.benchmark_75;
      } else if (this.compareTo == "vs_90th") {
        if (item.benchmark_90 == null) return null;
        return item.score - item.benchmark_90;
      } else if (this.compareTo == "vs_prev") {
        if (item.previous_score == null) return null;
        return item.score - item.previous_score;
      } else if (this.compareTo == "vs_org") {
        if (item.org_score == null) return null;
        return item.score - item.org_score;
      }
    },
  };
}
